import React, { useCallback, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { proxySetIdState } from 'venn-state';
import { LoadingSize, Notifications, NotificationType, Spinner } from 'venn-ui-kit';
import { plural, useHasFF } from 'venn-utils';
import { removeFundProxy, type DetailedProxyMetadata } from 'venn-api';
import type { FundToBulkProxy } from '../modals/pickers/types';
import type { BulkPickerDetailedProxyMetadata } from '../hooks/useProxyDataQuery';
import ConfirmationModal from '../modals/confirmation/ConfirmationModal';
import { partition, compact, flatMap } from 'lodash';

type DeleteProxiesConfirmationModalProps = {
  fundsWithProxy: FundToBulkProxy[];
  proxyDataByFund: Record<string, BulkPickerDetailedProxyMetadata[]>;
  holdingsProxyDataByFund?: Record<string, DetailedProxyMetadata | undefined>;
  onCancel: () => void;
  onStartingUpdate?: (updatingFunds: string[]) => void;
  onFinishedUpdate?: (
    updatedFundIds: string[],
    failedFundIds: string[],
    details: { hasReturnsProxies?: boolean; hasHoldingsProxies?: boolean },
  ) => void;
};

export const DeleteProxiesConfirmationModal = ({
  fundsWithProxy,
  proxyDataByFund,
  holdingsProxyDataByFund,
  onCancel,
  onStartingUpdate,
  onFinishedUpdate,
}: DeleteProxiesConfirmationModalProps) => {
  const proxySetId = useRecoilValue(proxySetIdState);
  const [isProxiesUpdating, setIsProxiesUpdating] = useState(false);
  const holdingProxyFF = useHasFF('holding_proxy_ff');

  const deleteProxies = useCallback(async () => {
    if (isProxiesUpdating) {
      return;
    }
    setIsProxiesUpdating(true);
    onStartingUpdate?.(fundsWithProxy.map((fund) => fund.id));
    try {
      type ProxyFundToDelete = {
        id: string;
        variant: 'RETURN' | 'HOLDING';
      };
      type Result = Success | Failure;
      type Success = {
        id: string;
        variant: 'RETURN' | 'HOLDING';
        status: 'success';
      };
      type Failure = {
        id: string;
        variant: 'RETURN' | 'HOLDING';
        status: 'error';
        error: unknown;
      };

      const hasReturnsProxies = fundsWithProxy.some((fund) => (proxyDataByFund[fund.id]?.length ?? 0) > 0);
      const hasHoldingsProxies =
        holdingProxyFF && fundsWithProxy.some((fund) => holdingsProxyDataByFund?.[fund.id]?.proxyId);

      const fundsToDelete: ProxyFundToDelete[] = flatMap(fundsWithProxy, (fund) =>
        compact([
          ...(proxyDataByFund[fund.id] ?? []).map(() => ({ id: fund.id, variant: 'RETURN' as const })),
          holdingProxyFF && holdingsProxyDataByFund?.[fund.id]?.proxyId && { id: fund.id, variant: 'HOLDING' as const },
        ]),
      );

      const results: Result[] = await Promise.all(
        fundsToDelete.map((fund) =>
          removeFundProxy(fund.id, proxySetId, fund.variant).then(
            () => ({ id: fund.id, variant: fund.variant, status: 'success' as const }),
            (error) => ({ id: fund.id, variant: fund.variant, status: 'error' as const, error }),
          ),
        ),
      );

      const isSuccess = (result: Result): result is Success => result.status === 'success';
      const [fulfilledResults, failedResults] = partition(results, isSuccess);

      onFinishedUpdate?.(
        fulfilledResults.map((result) => result.id),
        failedResults.map((result) => result.id),
        { hasReturnsProxies, hasHoldingsProxies },
      );

      const numSuccess = fulfilledResults.length;
      const numFail = failedResults.length;
      numSuccess > 0 && Notifications.notify(`Removed ${numSuccess} proxies successfully.`, NotificationType.SUCCESS);
      numFail > 0 && Notifications.notify(`Failed to remove ${numFail} proxies.`, NotificationType.ERROR);
    } catch (e) {
      onFinishedUpdate?.(
        [],
        fundsWithProxy.map((fund) => fund.id),
        {},
      );
      Notifications.notify('Failed to remove proxies.', NotificationType.ERROR);
    } finally {
      setIsProxiesUpdating(false);
    }
  }, [
    fundsWithProxy,
    proxyDataByFund,
    holdingsProxyDataByFund,
    holdingProxyFF,
    isProxiesUpdating,
    onFinishedUpdate,
    onStartingUpdate,
    proxySetId,
  ]);

  const uniqueProxyCount = useMemo(() => {
    const allProxyIds = flatMap(fundsWithProxy, (fund) =>
      compact([
        ...(proxyDataByFund[fund.id] ?? []).map((proxy) => proxy.proxyId),
        holdingProxyFF ? holdingsProxyDataByFund?.[fund.id]?.proxyId : undefined,
      ]),
    );

    return new Set(allProxyIds).size;
  }, [fundsWithProxy, proxyDataByFund, holdingsProxyDataByFund, holdingProxyFF]);

  return (
    <ConfirmationModal
      destructive
      header={plural(uniqueProxyCount, { 1: 'Remove proxy?', other: 'Remove proxies?' })}
      text={plural(uniqueProxyCount, {
        1: 'Removing this proxy will affect all portfolios, benchmarks, and views that include the proxied investment across your workspace.',
        other:
          'Removing these proxies will affect all portfolios, benchmarks, and views that include these proxied investments across your workspace.',
      })}
      onCancel={isProxiesUpdating ? undefined : onCancel}
      proceedLabel={
        isProxiesUpdating ? (
          <Spinner size={LoadingSize.micro} />
        ) : (
          plural(uniqueProxyCount, { 1: 'REMOVE PROXY', other: 'REMOVE PROXIES' })
        )
      }
      onProceed={deleteProxies}
      proceedDisabled={isProxiesUpdating}
    />
  );
};
